import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { motion } from 'motion/react';
import { formatPrice } from '../data/products';
import { useCart } from '../context/CartContext';
import { useState, useMemo, useEffect } from 'react';
import { resolveProductImage } from '../utils/productUtils';
import VariantBottomSheet from './VariantBottomSheet';

interface UGCProductCardProps {
  product: any;
  thumbnail?: string;
  isActive: boolean;
}

export default function UGCProductCard({ product, thumbnail, isActive }: UGCProductCardProps) {
  const navigate = useNavigate();
  const { addItem, setCartOpen } = useCart();

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const variants = useMemo(() => product?.product_variants || product?.variants || [], [product]);
  const firstVariant = variants[0];
  const hasMultipleVariants = variants.length > 1;
  const shortName = product?.short_name || product?.shortName || product?.name || '';

  const originalPrice = firstVariant?.price ?? 0;
  const promoPrice = firstVariant?.promo_price || firstVariant?.promoPrice;
  const hasPromo = !!promoPrice && promoPrice > 0 && promoPrice < originalPrice;
  const displayPrice = hasPromo ? promoPrice : originalPrice;

  const colors = useMemo<string[]>(
    () => [...new Set(variants.map((v: any) => v.color).filter(Boolean) as string[])],
    [variants]
  );

  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>('');

  useEffect(() => {
    if (!isPickerOpen) return;
    if (!colors.length) return;
    if (!selectedColor) setSelectedColor(colors[0]);
  }, [colors, isPickerOpen, selectedColor]);

  const colorVariants = useMemo(
    () => variants.filter((v: any) => v.color === selectedColor),
    [variants, selectedColor]
  );

  const options = useMemo<string[]>(
    () =>
      [...new Set(colorVariants.map((v: any) => v.option_name || v.option).filter(Boolean) as string[])],
    [colorVariants]
  );

  useEffect(() => {
    if (!isPickerOpen) return;
    if (!options.length) return;
    if (!selectedOption) setSelectedOption(options[0]);
  }, [isPickerOpen, options, selectedOption]);

  const activeVariant = useMemo(() => {
    if (!isPickerOpen) return firstVariant;
    return (
      colorVariants.find((v: any) => (v.option_name || v.option) === selectedOption) ||
      colorVariants[0] ||
      firstVariant
    );
  }, [colorVariants, firstVariant, isPickerOpen, selectedOption]);

  if (!product) return null;

  const commitAdd = (variant: any) => {
    if (!variant) return;
    const normalizedColor = String(variant.color || '').trim().toLowerCase();
    const groupForColor = (product.variant_groups || []).find(
      (g: any) => String(g.color || '').trim().toLowerCase() === normalizedColor
    );
    const groupFirstImage = (groupForColor?.images || [])
      .map((img: any) => (typeof img === 'string' ? img : img?.image_url))
      .find(Boolean);
    const variantImage = groupFirstImage || variant.image_url || variant.image || product.thumbnail_url || product.thumbnailUrl;

    addItem({
      sku: variant.sku,
      productId: product.id,
      productName: shortName,
      color: variant.color,
      option: variant.option_name || variant.option,
      price: variant.price,
      promoPrice: variant.promo_price || variant.promoPrice,
      quantity: 1,
      image: resolveProductImage(variantImage),
      stockLimit: variant.stock,
    });
    setCartOpen(true);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasMultipleVariants) {
      setIsPickerOpen(true);
    } else if (firstVariant) {
      commitAdd(firstVariant);
    }
  };

  return (
    <motion.div
      animate={{
        opacity: 1,
        y: 0,
        scale: isActive ? 1.02 : 1
      }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="w-full"
    >
      <Link
        to={product.slug ? `/koleksi/${product.slug}` : `/product/${product.id}`}
        className={`group flex items-center gap-3 py-2 bg-transparent border-0 rounded-none transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-70 hover:opacity-100'
          }`}
      >
        {/* Aspect Ratio 3:4 Thumbnail */}
        <div className="w-14 h-[74px] overflow-hidden bg-zinc-50 shrink-0 border-0 rounded-none">
          <img
            src={thumbnail}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
        </div>

        <div className="flex-1 min-w-0 pr-1">
          <h3 className="text-xs md:text-sm font-medium text-ink tracking-tight truncate font-sans group-hover:text-brand-wine transition-colors duration-300">
            {shortName}
          </h3>

          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap items-baseline gap-1.5">
              {hasPromo && (
                <span className="text-[10px] md:text-xs text-muted/70 line-through tabular-nums leading-none font-medium">
                  {formatPrice(originalPrice)}
                </span>
              )}
              <span className="text-xs md:text-sm font-semibold tracking-tighter text-ink tabular-nums leading-none">
                {formatPrice(displayPrice)}
              </span>
            </div>

            <button
              onClick={handleAddToCart}
              className="w-7 h-7 bg-brand-wine text-white flex items-center justify-center shrink-0 rounded-none transition-all duration-300 hover:bg-brand-wine-deep active:scale-[0.88] ease-[cubic-bezier(0.16,1,0.3,1)]"
              aria-label="Tambah ke keranjang"
            >
              <Plus className="w-4 h-4 transition-transform duration-300 active:scale-90" />
            </button>
          </div>
        </div>
      </Link>

      <VariantBottomSheet
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        product={product}
        variants={variants}
        colors={colors}
        selectedColor={selectedColor}
        setSelectedColor={setSelectedColor}
        options={options}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        activeVariant={activeVariant}
        onAdd={() => {
          commitAdd(activeVariant);
          setIsPickerOpen(false);
        }}
        shortName={shortName}
      />
    </motion.div>
  );
}
